import { db } from '$db/local/db';
import { recordConflict } from '../repositories/conflicts.repo';
import { localTable } from './tables';
import { objectToCamelCase } from './case';
import { withSuppression } from './bridge';
import type { RemoteRow } from './pull';

export interface MergeStats {
  inserted: number;
  updated: number;
  conflicts: number;
  skipped: number;
}

/**
 * Aplica filas remotas a la BD local con LWW por fila.
 * - Regla general: si remote.updated_at > local.updated_at → aplicar remoto.
 * - Si local.updated_at > remote.updated_at y hay op pendiente → conflicto
 *   (registrar en `conflicts` con resolution='local_won', no tocar local).
 * - review_events: append-only, insert si no existe.
 */
export async function mergeRemote(
  rows: RemoteRow[],
  userId: string
): Promise<MergeStats> {
  const stats: MergeStats = {
    inserted: 0,
    updated: 0,
    conflicts: 0,
    skipped: 0
  };

  if (rows.length === 0) return stats;

  const pendingKeys = await loadPendingKeys(userId);

  return withSuppression(async () => {
    await mergeRowsIntoLocal(rows, pendingKeys, stats);
    return stats;
  });
}

/**
 * Fusiona dos mapas de nodeTimes.
 * - Si una clave existe en ambos, toma el MÁXIMO (evita duplicados por re-sync).
 * - Si existe en uno solo, la conserva.
 * Esto garantiza que el tiempo de estudio de ambos dispositivos se preserve
 * sin duplicar cuando el mismo dispositivo sincroniza varias veces.
 */
function mergeNodeTimes(
  local: Record<string, number> | null | undefined,
  remote: Record<string, number> | null | undefined
): Record<string, number> {
  const result: Record<string, number> = { ...(local ?? {}) };
  if (!remote) return result;
  for (const [nodeId, ms] of Object.entries(remote)) {
    if (typeof ms !== 'number') continue;
    const existing = result[nodeId] ?? 0;
    result[nodeId] = Math.max(existing, ms);
  }
  return result;
}

async function mergeRowsIntoLocal(
  rows: RemoteRow[],
  pendingKeys: Set<string>,
  stats: MergeStats
): Promise<void> {
  for (const { entityType, row } of rows) {
    const table = localTable(entityType);
    if (!table) {
      stats.skipped += 1;
      continue;
    }

    const entity = objectToCamelCase(row);
    const id = entity.id;
    if (typeof id !== 'string') {
      stats.skipped += 1;
      continue;
    }

    if (entityType === 'reviewEvent') {
      const existing = await db.table(table).get(id);
      if (!existing) {
        await db.table(table).add(entity);
        stats.inserted += 1;
      } else {
        stats.skipped += 1;
      }
      continue;
    }

    const local = (await db.table(table).get(id)) as
      | Record<string, unknown>
      | undefined;

    if (!local) {
      await db.table(table).add(entity);
      stats.inserted += 1;
      continue;
    }

    const localUpdated = typeof local.updatedAt === 'string' ? local.updatedAt : '';
    const remoteUpdated =
      typeof entity.updatedAt === 'string' ? entity.updatedAt : '';

    if (remoteUpdated > localUpdated) {
      const hasPending = pendingKeys.has(`${entityType}|${id}`);
      if (hasPending) {
        await recordConflict({
          entityType,
          entityId: id,
          localVersion: local,
          remoteVersion: entity,
          localUpdatedAt: localUpdated,
          remoteUpdatedAt: remoteUpdated,
          resolution: 'remote_won'
        });
        stats.conflicts += 1;
      }

      // Merge especial para macroSession: preservar nodeTimes de ambos lados
      if (entityType === 'macroSession') {
        const localTimes = (local as Record<string, unknown>).nodeTimes as
          | Record<string, number>
          | null
          | undefined;
        const remoteTimes = entity.nodeTimes as
          | Record<string, number>
          | null
          | undefined;
        entity.nodeTimes = mergeNodeTimes(localTimes, remoteTimes);
      }

      await db.table(table).put(entity);
      stats.updated += 1;
    } else if (localUpdated > remoteUpdated) {
      const hasPending = pendingKeys.has(`${entityType}|${id}`);
      if (hasPending) {
        await recordConflict({
          entityType,
          entityId: id,
          localVersion: local,
          remoteVersion: entity,
          localUpdatedAt: localUpdated,
          remoteUpdatedAt: remoteUpdated,
          resolution: 'local_won'
        });
        stats.conflicts += 1;
      } else {
        stats.skipped += 1;
      }
    } else {
      stats.skipped += 1;
    }
  }
}

async function loadPendingKeys(userId: string): Promise<Set<string>> {
  const ops = await db.outbox.where('userId').equals(userId).toArray();
  const s = new Set<string>();
  for (const op of ops) {
    if (op.status === 'pending' || op.status === 'syncing') {
      s.add(`${op.entityType}|${op.entityId}`);
    }
  }
  return s;
}